import { flow, Instance, types } from 'mobx-state-tree';
import axios from 'axios';
import OpenPayLib, { getDeviceId } from '@utils/openPay';
import sendPaymentError from '@utils/sendPaymentError';
import logPayment from '@utils/logPayment';
import { Partner } from '@constants/paymentLink';
import { PaymentMethod } from '@store/checkout';
import { sendEvent } from '@utils/ga';
import { ACTIONS, CATEGORIES } from '@constants/analytics';
import { IPayeeInfo } from '@containers/checkout/payment-form/types';
import endpoints from '@configs/endpoints';
import { getConfirmationUrl } from '@utils/getUrlParam';
import getCardInfo from '@utils/getCardInfo';
import Common from '@store/common';
import { initOpenPay } from '@utils/openPay';
import { isProd } from '@configs/variables';
export interface OpenPayTokenParameters {
  card_number: string;
  holder_name: string;
  expiration_year: string;
  expiration_month: string;
  cvv2: string;
}

export interface FormPayload extends IPayeeInfo {
  cardNumber: string;
  cvv2: string;
  expiration: string;
  holderName: string;
}

export interface PayPayload extends FormPayload {
  paymentLinkId: string;
  isSura: boolean;
  subscribeCard?: boolean;
  interestFreeMonths?: string;
  suraSubscriptionApiKeyRequired?: boolean;
}

export interface CreateCardSubscriptionPayload extends FormPayload {
  paymentLinkId: string;
  isSura: boolean;
  cardId?: string;
  suraSubscriptionApiKeyRequired?: boolean;
}

export interface Confirm3dsPaymentStatusPayload {
  transactionId: string;
  partner: string;
}

const gaEvent = {
  category: CATEGORIES.CHECKOUT.CATEGORY,
  action: ACTIONS.SUBMIT,
};

const handleCardAcceptedEvent = () => {
  sendEvent({
    ...gaEvent,
    label: CATEGORIES.CHECKOUT.LABELS.CREDIT_CARD_ACCEPTED,
  });
};

const handleCardDeclinedEvent = (error?: string) => {
  sendEvent({
    ...gaEvent,
    label: CATEGORIES.CHECKOUT.LABELS.CREDIT_CARD_DECLINED,
    dimension1: error,
  });

  throw new Error('3DSecure Error');
};

const errorCodes = {
  '2004': 'El número de tarjeta no es válido. Verifica e intenta de nuevo.',
  '2005':
    'Parece que la tarjeta ya venció. Revisa la fecha de expiración o intenta con otra tarjeta.',
  '2006': 'Falta el código de seguridad (CVV). Agrégalo para continuar.',
  '2007': 'Esta tarjeta no es válida. Intenta con otra o verifica los datos.',
  '2008': 'Esta tarjeta no permite pagar con puntos. Usa otra forma de pago.',
  '2009': 'El código de seguridad (CVV) no es válido. Revísalo e intenta de nuevo.',
  '2010': 'No se pudo validar la tarjeta con tu banco. Intenta de nuevo o contacta a tu banco.',
  '3001': 'Tarjeta declinada, comunícate con tu banco',
  '3002': 'Esta tarjeta ya expiró. Usa una diferente o revisa la fecha.',
  '3003': 'Fondos insuficientes',
  '3004': 'No pudimos procesar el pago. Contacta a tu banco para más información.',
  '3005': 'No pudimos procesar el pago. Contacta a tu banco para más información.',
  '3006': 'No pudimos procesar el pago. Contacta a tu banco para más información.',
  '3008': 'No pudimos procesar el pago. Contacta a tu banco para más información.',
  '3009': 'No pudimos procesar el pago. Contacta a tu banco para más información.',
  '3010': 'No pudimos procesar el pago. Contacta a tu banco para más información.',
  '3011': 'No pudimos procesar el pago. Contacta a tu banco para más información.',
  '3012': 'No pudimos procesar el pago. Contacta a tu banco para más información.',
  '3201': 'Este comercio no permite pagos a meses sin intereses con esta tarjeta.',
  '3203': 'Esta promoción no aplica para tu tipo de tarjeta.',
  '3204': 'El monto es menor al mínimo requerido para aplicar esta promoción.',
  '3205': 'Esta promoción no está disponible con los datos ingresados.',
};

const OpenPay = types
  .compose(
    Common,
    types.model({
      flags: types.optional(
        types.model({
          isRequestingToken: false,
          isSendingPayment: false,
        }),
        {},
      ),
      errorMessage: types.optional(types.string, ''),
    }),
  )
  .volatile(() => ({
    deviceIdRecord: '',
  }))
  .views((self) => ({
    get isProcessing() {
      return self.flags.isRequestingToken || self.flags.isSendingPayment;
    },
    get deviceId() {
      if (self.deviceIdRecord) return self.deviceIdRecord;

      const deviceId = getDeviceId();
      if (deviceId) {
        self.deviceIdRecord = deviceId;
      }

      return deviceId;
    },
  }))
  .actions((self) => ({
    setIsRequestingTokenFalse: () => {
      self.flags.isRequestingToken = false;
    },
    setErrorMessage: (errorCode: string | any) => {
      let code = '';

      if (errorCode === undefined || errorCode === null) {
        self.errorMessage = 'Error desconocido. Por favor, intenta de nuevo o contacta a soporte.';
        return;
      }
      if (typeof errorCode === 'object' && errorCode.data && errorCode.data.error_code) {
        code = errorCode.data.error_code.toString();
      } else if (typeof errorCode === 'object' && errorCode.errors && errorCode.errors.error_code) {
        code = errorCode.errors.error_code.toString();
      } else if (typeof errorCode === 'object' && errorCode.error_code) {
        code = errorCode.error_code.toString();
      } else if (typeof errorCode === 'string') {
        code = errorCode;
      } else if (typeof errorCode === 'number') {
        code = errorCode.toString();
      }
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const errorMessage = code && errorCodes[code];
      self.errorMessage = errorMessage || 'No se puede procesar el cargo, comunícate con tu Banco';
    },
  }))
  .actions((self) => ({
    generateToken: flow(function* (parameters: OpenPayTokenParameters) {
      self.flags.isRequestingToken = true;

      try {
        return yield new Promise((res, rej) => {
          OpenPayLib.token.create(
            parameters,
            (value: any) => {
              self.setIsRequestingTokenFalse();
              res(value);
            },
            (error: any) => {
              self.setIsRequestingTokenFalse();
              self.setErrorMessage(error);
              rej(error);
            },
          );
        });
      } catch (error) {
        self.setIsRequestingTokenFalse();
        throw error;
      }
    }),
  }))
  .actions((self) => ({
    pay: flow(function* (parameters: PayPayload) {
      self.flags.isSendingPayment = true;
      self.errorMessage = '';
      const [month, year] = parameters.expiration.split('/');
      const tokenPayload: OpenPayTokenParameters = {
        card_number: parameters.cardNumber.replaceAll(' ', ''),
        holder_name: parameters.holderName,
        expiration_month: month,
        expiration_year: year,
        cvv2: parameters.cvv2,
      };

      const paymentLinkInfo = {
        payeeFirstName: parameters.payeeFirstName,
        payeeLastName: parameters.payeeLastName,
        payeeEmail: parameters.payeeEmail.trim(),
        payeePhone: parameters.payeePhone.replaceAll(' ', ''),
        paymentLinkId: parameters.paymentLinkId,
        partner: Partner.OPENPAY,
      };

      const { bin, last4 } = getCardInfo(tokenPayload.card_number);
      const cardInfo = {
        holderName: tokenPayload.holder_name,
        bin,
        last4,
      };

      try {
        let tokenResponse;
        try {
          tokenResponse = yield self.generateToken(tokenPayload);
        } catch (tokenError) {
          yield Promise.resolve(
            sendPaymentError({
              ...paymentLinkInfo,
              paymentMethod: PaymentMethod.card,
              error: JSON.stringify(tokenError),
              cardInfo,
            }),
          );
          throw tokenError;
        }
        if (!tokenResponse?.data?.id) {
          throw new Error('No se pudo generar un token válido');
        }
        const token = tokenResponse.data.id;

        let subscribeCardToken;
        if (parameters?.subscribeCard) {
          const { paymentLink } = self.globalStore.links;

          if (parameters?.suraSubscriptionApiKeyRequired) {
            yield Promise.resolve(
              initOpenPay({
                apiKey: paymentLink.sura.subscriptionPublicApiKey,
                id: paymentLink.sura.subscriptionMerchantId,
                isProd,
              }),
            );
          }

          let subscriptionTokenResponse;
          try {
            subscriptionTokenResponse = yield self.generateToken(tokenPayload);
          } catch (subscriptionTokenError) {
            yield Promise.resolve(
              sendPaymentError({
                ...paymentLinkInfo,
                paymentMethod: PaymentMethod.card,
                error: JSON.stringify(subscriptionTokenError),
                cardInfo,
              }),
            );
            throw subscriptionTokenError;
          }
          if (!subscriptionTokenResponse?.data?.id) {
            throw new Error('No se pudo generar un token de suscripción válido');
          }
          subscribeCardToken = subscriptionTokenResponse.data.id;
        }

        const payload = {
          ...paymentLinkInfo,
          token,
          deviceId: self.deviceId,
          confirmationUrl: getConfirmationUrl(parameters.paymentLinkId, parameters.isSura),
          cardInfo,
          ...(parameters.interestFreeMonths && {
            interestFreeMonths: parameters.interestFreeMonths,
          }),
          subscribeCardToken,
          subscribeCard: parameters?.subscribeCard || false,
        };

        const { links } = self.globalStore;
        const { paymentLink } = links;
        const { isSubscription } = paymentLink;

        const { data } = yield axios.post(
          !isSubscription ? endpoints.processCardPayment : endpoints.processCardSubscription,
          payload,
        );

        return Promise.resolve(data);
      } catch (error) {
        self.setErrorMessage(error);
        throw error;
      } finally {
        self.flags.isSendingPayment = false;
      }
    }),
    confirm3dsPaymentStatus: flow(function* (parameters: Confirm3dsPaymentStatusPayload) {
      const { links } = self.globalStore;
      const { setIsRequesting } = links;
      setIsRequesting(true);
      try {
        const { data } = yield axios.post(endpoints.confirm3dsPaymentStatus, parameters);
        return data;
        // eslint-disable-next-line no-useless-catch
      } catch (e: any) {
        logPayment({
          businessId: links.paymentLink.businessId,
          parentPartnerId: '',
          type: 'ERROR',
          source: 'AR_FRONT',
          paymentPartner: 'OPENPAY',
          paymentMethod: 'CARD_PAYMENT',
          key: '[AR] - ERROR - OPEN_PAY_CONFIRM_3DS_PAYMENT_STATUS',
          code: '04',
          description: `Error when confirming 3ds payment with Oyster. PaymentLinkId: ${links.paymentLink.paymentLinkId}`,
          metadata: JSON.stringify({ e, parameters }),
          paymentLinkId: links.paymentLink.paymentLinkId,
        });
        throw e;
      } finally {
        setIsRequesting(false);
      }
    }),
    createCardSubscription: flow(function* (parameters: CreateCardSubscriptionPayload) {
      self.errorMessage = '';
      const [month, year] = parameters.expiration.split('/');
      const tokenPayload: OpenPayTokenParameters = {
        card_number: parameters.cardNumber.replaceAll(' ', ''),
        holder_name: parameters.holderName,
        expiration_month: month,
        expiration_year: year,
        cvv2: parameters.cvv2,
      };

      const paymentLinkInfo = {
        payeeFirstName: parameters.payeeFirstName,
        payeeLastName: parameters.payeeLastName,
        payeeEmail: parameters.payeeEmail.trim(),
        payeePhone: parameters.payeePhone.replaceAll(' ', ''),
        paymentLinkId: parameters.paymentLinkId,
        partner: Partner.OPENPAY,
        cardId: parameters?.cardId || '',
      };

      const { bin, last4 } = getCardInfo(tokenPayload.card_number);
      const cardInfo = {
        holderName: tokenPayload.holder_name,
        bin,
        last4,
      };

      try {
        if (parameters?.suraSubscriptionApiKeyRequired) {
          const { paymentLink } = self.globalStore.links;
          yield Promise.resolve(
            initOpenPay({
              apiKey: paymentLink.sura.subscriptionPublicApiKey,
              id: paymentLink.sura.subscriptionMerchantId,
              isProd,
            }),
          );
        }

        let tokenResponse;
        try {
          tokenResponse = yield self.generateToken(tokenPayload);
        } catch (tokenError) {
          yield Promise.resolve(
            sendPaymentError({
              ...paymentLinkInfo,
              paymentMethod: PaymentMethod.card,
              error: JSON.stringify(tokenError),
              cardInfo,
            }),
          );
          throw tokenError;
        }
        if (!tokenResponse?.data?.id) {
          throw new Error('No se pudo generar un token válido');
        }
        const token = tokenResponse.data.id;

        const payload = {
          ...paymentLinkInfo,
          token,
          deviceId: self.deviceId,
          confirmationUrl: getConfirmationUrl(parameters.paymentLinkId, parameters.isSura),
          cardInfo,
        };

        const { data } = yield axios.post(endpoints.processCardSubscription, payload);
        return Promise.resolve(data);
      } catch (error) {
        self.setErrorMessage(error);
        throw error;
      }
    }),
  }));

export interface OpenPayStore extends Instance<typeof OpenPay> {}
export default OpenPay;
